import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getMindMap, saveMindMap } from '../../api/mindmap';
import { BRANCH_COLORS, DEFAULT_SHAPE_FILL, DEFAULT_SHAPE_STROKE, ROOT_COLOR } from './palette';
import { deriveTitle, getDescendantIds, getVisibleNodes } from './tree';
import { DEFAULT_SIZE, nextChildPosition, tidyLayout } from './layout';

const HISTORY_LIMIT = 100;
const SAVE_DEBOUNCE_MS = 800;
const RETRY_DELAY_MS = 5000;
const TIDY_DURATION_MS = 280;
const NEW_SHAPE = { width: 240, height: 180 };
const SHAPE_GAP = 16;
const SHAPE_STEP = 48;

const EMPTY_DOC = { nodes: [], shapes: [] };

const round = (value) => Math.round(value * 100) / 100;

const fromServer = (node) => ({
  id: node.id,
  parentId: node.parentId ?? null,
  text: node.text ?? '',
  note: node.note ?? '',
  color: node.color ?? ROOT_COLOR,
  done: !!node.done,
  collapsed: !!node.collapsed,
  x: node.x,
  y: node.y,
});

const toServer = (node) => ({
  id: node.id,
  parentId: node.parentId ?? null,
  text: node.text,
  note: node.note ? node.note : null,
  color: node.color,
  done: !!node.done,
  collapsed: !!node.collapsed,
  x: round(node.x),
  y: round(node.y),
});

const shapeFromServer = (shape) => ({
  id: shape.id,
  kind: shape.kind === 'CHECKLIST' ? 'CHECKLIST' : 'TEXT',
  x: shape.x,
  y: shape.y,
  width: shape.width,
  height: shape.height,
  text: shape.text ?? '',
  items: (shape.items ?? []).map((item) => ({ id: item.id, text: item.text ?? '', done: !!item.done })),
  fill: shape.fill ?? DEFAULT_SHAPE_FILL,
  stroke: shape.stroke ?? DEFAULT_SHAPE_STROKE,
});

const shapeToServer = (shape) => ({
  id: shape.id,
  kind: shape.kind,
  x: round(shape.x),
  y: round(shape.y),
  width: round(shape.width),
  height: round(shape.height),
  text: shape.kind === 'TEXT' && shape.text ? shape.text : null,
  items:
    shape.kind === 'CHECKLIST'
      ? shape.items.filter((item) => item.text.trim()).map(({ id, text, done }) => ({ id, text, done }))
      : [],
  fill: shape.fill,
  stroke: shape.stroke,
});

const textToItems = (text) =>
  text
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
    .filter(Boolean)
    .map((line) => ({ id: uuidv4(), text: line, done: false }));

const itemsToText = (items) =>
  items
    .map((item) => item.text.trim())
    .filter(Boolean)
    .join('\n');

export const newChecklistItem = () => ({ id: uuidv4(), text: '', done: false });

export const useMindMapEditor = (mindMapId) => {
  const [loadState, setLoadState] = useState({ status: 'loading', message: '' });
  const [reloadKey, setReloadKey] = useState(0);
  const [doc, setDoc] = useState(EMPTY_DOC);
  const [sizes, setSizes] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [editing, setEditingState] = useState(null);
  const [saveState, setSaveState] = useState({ status: 'saved', message: '' });
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });

  const docRef = useRef(EMPTY_DOC);
  const editingRef = useRef(null);
  const sizesRef = useRef({});
  const versionRef = useRef(0);
  const past = useRef([]);
  const future = useRef([]);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const conflictRef = useRef(false);
  const saveTimer = useRef(null);
  const retryTimer = useRef(null);
  const tweenRef = useRef(0);
  const pendingSizes = useRef({});
  const sizeFrame = useRef(0);

  sizesRef.current = sizes;

  const setEditing = useCallback((value) => {
    editingRef.current = value;
    setEditingState(value);
  }, []);

  const syncHistory = useCallback(() => {
    setHistory({ canUndo: past.current.length > 0, canRedo: future.current.length > 0 });
  }, []);

  const flushSave = useCallback(async () => {
    clearTimeout(saveTimer.current);
    clearTimeout(retryTimer.current);
    if (savingRef.current || conflictRef.current || !dirtyRef.current) return;

    savingRef.current = true;
    setSaveState({ status: 'saving', message: '' });
    try {
      while (dirtyRef.current) {
        dirtyRef.current = false;
        const { nodes, shapes } = docRef.current;
        const saved = await saveMindMap(mindMapId, {
          title: deriveTitle(nodes),
          version: versionRef.current,
          nodes: nodes.map(toServer),
          shapes: shapes.map(shapeToServer),
        });
        versionRef.current = saved.version;
      }
      setSaveState({ status: 'saved', message: '' });
    } catch (error) {
      dirtyRef.current = true;
      if (error.status === 409) {
        conflictRef.current = true;
        setSaveState({ status: 'conflict', message: error.message });
      } else {
        const retryable = error.status === undefined || error.status >= 500;
        setSaveState({ status: 'error', message: error.message });
        if (retryable) retryTimer.current = setTimeout(flushSave, RETRY_DELAY_MS);
      }
    } finally {
      savingRef.current = false;
    }
  }, [mindMapId]);

  const commit = useCallback(
    (next) => {
      docRef.current = next;
      setDoc(next);
      dirtyRef.current = true;
      if (conflictRef.current) return;
      setSaveState((prev) => (prev.status === 'saving' ? prev : { status: 'dirty', message: '' }));
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
    },
    [flushSave]
  );

  const pushHistory = useCallback(() => {
    past.current.push(docRef.current);
    if (past.current.length > HISTORY_LIMIT) past.current.shift();
    future.current = [];
    syncHistory();
  }, [syncHistory]);

  const mutateNodes = useCallback(
    (updater, { record = true } = {}) => {
      if (record) pushHistory();
      commit({ ...docRef.current, nodes: updater(docRef.current.nodes) });
    },
    [commit, pushHistory]
  );

  const mutateShapes = useCallback(
    (updater, { record = true } = {}) => {
      if (record) pushHistory();
      commit({ ...docRef.current, shapes: updater(docRef.current.shapes) });
    },
    [commit, pushHistory]
  );

  useEffect(() => {
    let cancelled = false;
    setLoadState({ status: 'loading', message: '' });
    getMindMap(mindMapId)
      .then((map) => {
        if (cancelled) return;
        const loaded = { nodes: map.nodes.map(fromServer), shapes: (map.shapes ?? []).map(shapeFromServer) };
        versionRef.current = map.version;
        docRef.current = loaded;
        past.current = [];
        future.current = [];
        dirtyRef.current = false;
        conflictRef.current = false;
        setDoc(loaded);
        setSelectedId(null);
        setSelectedShapeId(null);
        setEditing(null);
        setSaveState({ status: 'saved', message: '' });
        syncHistory();
        setLoadState({ status: 'ready', message: '' });
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadState({ status: error.status === 404 ? 'notFound' : 'error', message: error.message });
      });
    return () => {
      cancelled = true;
    };
  }, [mindMapId, reloadKey, setEditing, syncHistory]);

  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') flushSave();
    };
    const warnWhenUnsaved = (event) => {
      if (dirtyRef.current || savingRef.current) event.preventDefault();
    };
    document.addEventListener('visibilitychange', flushWhenHidden);
    window.addEventListener('pagehide', flushSave);
    window.addEventListener('online', flushSave);
    window.addEventListener('beforeunload', warnWhenUnsaved);
    return () => {
      document.removeEventListener('visibilitychange', flushWhenHidden);
      window.removeEventListener('pagehide', flushSave);
      window.removeEventListener('online', flushSave);
      window.removeEventListener('beforeunload', warnWhenUnsaved);
      cancelAnimationFrame(tweenRef.current);
      cancelAnimationFrame(sizeFrame.current);
      flushSave();
    };
  }, [flushSave]);

  const reportSize = useCallback((id, w, h) => {
    const current = sizesRef.current[id];
    const pending = pendingSizes.current[id];
    const known = pending ?? current;
    if (known && Math.abs(known.w - w) < 0.5 && Math.abs(known.h - h) < 0.5) return;

    pendingSizes.current[id] = { w, h };
    if (sizeFrame.current) return;
    sizeFrame.current = requestAnimationFrame(() => {
      sizeFrame.current = 0;
      const batch = pendingSizes.current;
      pendingSizes.current = {};
      setSizes((prev) => ({ ...prev, ...batch }));
    });
  }, []);

  const closeEditor = useCallback((id) => {
    const current = editingRef.current;
    if (!current || (typeof id === 'string' && id !== current.id)) return;
    setEditing(null);

    if (current.target === 'shape') {
      const shape = docRef.current.shapes.find((candidate) => candidate.id === current.id);
      if (shape?.kind === 'CHECKLIST' && shape.items.some((item) => !item.text.trim())) {
        commit({
          ...docRef.current,
          shapes: docRef.current.shapes.map((candidate) =>
            candidate.id === shape.id ? { ...candidate, items: candidate.items.filter((item) => item.text.trim()) } : candidate
          ),
        });
      }
      return;
    }

    const { nodes } = docRef.current;
    const node = nodes.find((candidate) => candidate.id === current.id);
    const hasChildren = nodes.some((candidate) => candidate.parentId === current.id);
    if (!current.isNew || !node || node.text.trim() || node.note.trim() || hasChildren) return;

    if (past.current.at(-1) === current.baseline) {
      past.current.pop();
      syncHistory();
    }
    commit({ ...docRef.current, nodes: nodes.filter((candidate) => candidate.id !== current.id) });
    setSelectedId(node.parentId);
  }, [commit, setEditing, syncHistory]);

  const select = useCallback(
    (id) => {
      if (editingRef.current && editingRef.current.id !== id) closeEditor();
      setSelectedId(id);
      setSelectedShapeId(null);
    },
    [closeEditor]
  );

  const selectShape = useCallback(
    (id) => {
      if (editingRef.current && editingRef.current.id !== id) closeEditor();
      setSelectedShapeId(id);
      setSelectedId(null);
    },
    [closeEditor]
  );

  const openEditor = useCallback(
    (id, mode = 'inline') => {
      const current = editingRef.current;
      if (current?.id === id) {
        if (current.mode !== mode) setEditing({ ...current, mode });
        return;
      }
      if (current) closeEditor();
      setSelectedId(id);
      setSelectedShapeId(null);
      setEditing({ id, target: 'node', isNew: false, baseline: null, recorded: false, mode });
    },
    [closeEditor, setEditing]
  );

  const openShapeEditor = useCallback(
    (id) => {
      if (editingRef.current?.id === id) return;
      if (editingRef.current) closeEditor();
      const shape = docRef.current.shapes.find((candidate) => candidate.id === id);
      if (!shape) return;
      if (shape.kind === 'CHECKLIST' && !shape.items.some((item) => !item.text.trim())) {
        commit({
          ...docRef.current,
          shapes: docRef.current.shapes.map((candidate) =>
            candidate.id === id ? { ...candidate, items: [...candidate.items, newChecklistItem()] } : candidate
          ),
        });
      }
      setSelectedShapeId(id);
      setSelectedId(null);
      setEditing({ id, target: 'shape', recorded: false });
    },
    [closeEditor, commit, setEditing]
  );

  const recordEditOnce = useCallback(() => {
    const session = editingRef.current;
    if (!session || session.recorded) return;
    session.recorded = true;
    pushHistory();
  }, [pushHistory]);

  const addChild = useCallback(
    (parentId) => {
      const current = docRef.current.nodes;
      const parent = current.find((node) => node.id === parentId);
      if (!parent) return;
      setEditing(null);

      const siblingCount = current.filter((node) => node.parentId === parentId).length;
      const color = parent.parentId == null ? BRANCH_COLORS[siblingCount % BRANCH_COLORS.length] : parent.color;
      const position = nextChildPosition(current, getVisibleNodes(current), parentId, sizesRef.current);
      const child = { id: uuidv4(), parentId, text: '', note: '', color, done: false, collapsed: false, ...position };

      mutateNodes((list) => [...list.map((node) => (node.id === parentId ? { ...node, collapsed: false } : node)), child]);
      setSelectedId(child.id);
      setSelectedShapeId(null);
      setEditing({
        id: child.id,
        target: 'node',
        isNew: true,
        baseline: past.current.at(-1),
        recorded: true,
        mode: 'inline',
      });
    },
    [mutateNodes, setEditing]
  );

  const addSibling = useCallback(
    (id) => {
      const node = docRef.current.nodes.find((candidate) => candidate.id === id);
      if (node?.parentId) addChild(node.parentId);
    },
    [addChild]
  );

  const updateNode = useCallback(
    (id, patch) => {
      recordEditOnce();
      mutateNodes((list) => list.map((node) => (node.id === id ? { ...node, ...patch } : node)), { record: false });
    },
    [mutateNodes, recordEditOnce]
  );

  const setNodeColor = useCallback(
    (id, color) => {
      recordEditOnce();
      mutateNodes(
        (list) => {
          const target = list.find((node) => node.id === id);
          const affected = target?.parentId == null ? new Set([id]) : new Set([id, ...getDescendantIds(list, id)]);
          return list.map((node) => (affected.has(node.id) ? { ...node, color } : node));
        },
        { record: false }
      );
    },
    [mutateNodes, recordEditOnce]
  );

  const deleteNode = useCallback(
    (id) => {
      const target = docRef.current.nodes.find((node) => node.id === id);
      if (!target || target.parentId == null) return;

      const removed = new Set([id, ...getDescendantIds(docRef.current.nodes, id)]);
      mutateNodes((list) => list.filter((node) => !removed.has(node.id)));
      setEditing(null);
      setSelectedId(target.parentId);
    },
    [mutateNodes, setEditing]
  );

  const toggleDone = useCallback(
    (id) => mutateNodes((list) => list.map((node) => (node.id === id ? { ...node, done: !node.done } : node))),
    [mutateNodes]
  );

  const toggleCollapsed = useCallback(
    (id) => mutateNodes((list) => list.map((node) => (node.id === id ? { ...node, collapsed: !node.collapsed } : node))),
    [mutateNodes]
  );

  const beginMove = useCallback(() => pushHistory(), [pushHistory]);

  const moveNodes = useCallback(
    (positions) =>
      mutateNodes((list) => list.map((node) => (positions[node.id] ? { ...node, ...positions[node.id] } : node)), {
        record: false,
      }),
    [mutateNodes]
  );

  const addShape = useCallback(
    (center) => {
      const { nodes, shapes } = docRef.current;
      const occupied = [
        ...getVisibleNodes(nodes).map((node) => {
          const { w, h } = sizesRef.current[node.id] ?? DEFAULT_SIZE;
          return { x: node.x - w / 2, y: node.y - h / 2, width: w, height: h };
        }),
        ...shapes,
      ];
      const overlaps = (x, y) =>
        occupied.some(
          (rect) =>
            x < rect.x + rect.width + SHAPE_GAP &&
            rect.x < x + NEW_SHAPE.width + SHAPE_GAP &&
            y < rect.y + rect.height + SHAPE_GAP &&
            rect.y < y + NEW_SHAPE.height + SHAPE_GAP
        );

      const startX = center.x - NEW_SHAPE.width / 2;
      const startY = center.y - NEW_SHAPE.height / 2;
      const candidates = Array.from({ length: 12 }, (_, ring) => ring * SHAPE_STEP).flatMap((d) => [
        [startX, startY + d],
        [startX + d, startY],
        [startX, startY - d],
        [startX - d, startY],
      ]);
      const [x, y] = candidates.find(([cx, cy]) => !overlaps(cx, cy)) ?? [startX, startY];

      if (editingRef.current) closeEditor();
      const shape = {
        id: uuidv4(),
        kind: 'TEXT',
        x,
        y,
        ...NEW_SHAPE,
        text: '',
        items: [],
        fill: DEFAULT_SHAPE_FILL,
        stroke: DEFAULT_SHAPE_STROKE,
      };
      mutateShapes((list) => [...list, shape]);
      setSelectedShapeId(shape.id);
      setSelectedId(null);
      setEditing({ id: shape.id, target: 'shape', recorded: true });
    },
    [closeEditor, mutateShapes, setEditing]
  );

  const editShape = useCallback(
    (id, patch) => {
      recordEditOnce();
      mutateShapes((list) => list.map((shape) => (shape.id === id ? { ...shape, ...patch } : shape)), { record: false });
    },
    [mutateShapes, recordEditOnce]
  );

  const updateShape = useCallback(
    (id, patch) => mutateShapes((list) => list.map((shape) => (shape.id === id ? { ...shape, ...patch } : shape))),
    [mutateShapes]
  );

  const setShapeKind = useCallback(
    (id, kind) => {
      const shape = docRef.current.shapes.find((candidate) => candidate.id === id);
      if (!shape || shape.kind === kind) return;
      updateShape(
        id,
        kind === 'CHECKLIST'
          ? { kind, items: textToItems(shape.text), text: '' }
          : { kind, text: itemsToText(shape.items), items: [] }
      );
    },
    [updateShape]
  );

  const toggleShapeItem = useCallback(
    (shapeId, itemId) => {
      const shape = docRef.current.shapes.find((candidate) => candidate.id === shapeId);
      if (!shape) return;
      const items = shape.items.map((item) => (item.id === itemId ? { ...item, done: !item.done } : item));
      if (editingRef.current?.id === shapeId) editShape(shapeId, { items });
      else updateShape(shapeId, { items });
    },
    [editShape, updateShape]
  );

  const moveShape = useCallback(
    (id, rect) =>
      mutateShapes((list) => list.map((shape) => (shape.id === id ? { ...shape, ...rect } : shape)), { record: false }),
    [mutateShapes]
  );

  const deleteShape = useCallback(
    (id) => {
      mutateShapes((list) => list.filter((shape) => shape.id !== id));
      if (editingRef.current?.id === id) setEditing(null);
      setSelectedShapeId(null);
    },
    [mutateShapes, setEditing]
  );

  const tidy = useCallback(() => {
    const target = tidyLayout(docRef.current.nodes, sizesRef.current);
    const origin = new Map(docRef.current.nodes.map((node) => [node.id, { x: node.x, y: node.y }]));
    pushHistory();
    cancelAnimationFrame(tweenRef.current);

    const startedAt = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / TIDY_DURATION_MS);
      const eased = 1 - Math.pow(1 - progress, 3);
      commit({
        ...docRef.current,
        nodes: docRef.current.nodes.map((node) => {
          const from = origin.get(node.id);
          const to = target[node.id];
          if (!from || !to) return node;
          return { ...node, x: from.x + (to.x - from.x) * eased, y: from.y + (to.y - from.y) * eased };
        }),
      });
      if (progress < 1) tweenRef.current = requestAnimationFrame(step);
    };
    tweenRef.current = requestAnimationFrame(step);
  }, [commit, pushHistory]);

  const restore = useCallback(
    (snapshot) => {
      cancelAnimationFrame(tweenRef.current);
      commit(snapshot);
      setEditing(null);
      setSelectedId((current) => (snapshot.nodes.some((node) => node.id === current) ? current : null));
      setSelectedShapeId((current) => (snapshot.shapes.some((shape) => shape.id === current) ? current : null));
      syncHistory();
    },
    [commit, setEditing, syncHistory]
  );

  const undo = useCallback(() => {
    const previous = past.current.pop();
    if (!previous) return;
    future.current.push(docRef.current);
    restore(previous);
  }, [restore]);

  const redo = useCallback(() => {
    const next = future.current.pop();
    if (!next) return;
    past.current.push(docRef.current);
    restore(next);
  }, [restore]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  const overwrite = useCallback(async () => {
    try {
      const latest = await getMindMap(mindMapId);
      versionRef.current = latest.version;
      conflictRef.current = false;
      dirtyRef.current = true;
      await flushSave();
    } catch (error) {
      setSaveState({ status: 'error', message: error.message });
    }
  }, [flushSave, mindMapId]);

  return {
    loadState,
    nodes: doc.nodes,
    shapes: doc.shapes,
    sizes,
    selectedId,
    selectedShapeId,
    editing,
    saveState,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    reportSize,
    select,
    selectShape,
    openEditor,
    openShapeEditor,
    closeEditor,
    addChild,
    addSibling,
    updateNode,
    setNodeColor,
    deleteNode,
    toggleDone,
    toggleCollapsed,
    beginMove,
    moveNodes,
    addShape,
    editShape,
    updateShape,
    setShapeKind,
    toggleShapeItem,
    moveShape,
    deleteShape,
    tidy,
    undo,
    redo,
    flushSave,
    reload,
    overwrite,
  };
};
